import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssignUserToTerminalDto, PlaceInListDto, TenantUsersApi, TenantUsersDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ProblemDetail, useModalErrorHandler } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-assign-parcel-manager',
  imports: [
    CommonModule,
    NzModalModule,
    ReactiveFormsModule,
    NzFormModule,
    NzSelectModule,
    NzButtonModule,
    NzSpinComponent,
    NzInputModule,
    ErrorDisplayComponent,
  ],
  standalone: true,
  template: `
    <nz-modal
      (nzOnCancel)="onHide()"
      (nzOnOk)="onOk()"
      [nzClosable]="true"
      [nzOkDisabled]="form.invalid || isSubmitting()"
      [nzVisible]="show"
      [nzWidth]="500"
      [nzOkLoading]="isSubmitting"
      nzOkText="Assign"
    >
      <div *nzModalTitle class="font-semibold">Assign Parcel Manager</div>
      <div *nzModalContent>
        <nz-spin [nzSpinning]="isSubmitting()">
          <app-error-display
            *ngIf="showTerminalsError()"
            [error]="terminalsErrorDetails"
            [showRetry]="true"
            (retry)="onRetryTerminals()"
            (dismiss)="onDismissTerminalsError()"
          ></app-error-display>
          <form
            [formGroup]="form"
            class="grid grid-cols-1 gap-4"
            nz-form
            nzLayout="vertical"
          >
            <nz-form-item>
              <nz-form-label [nzRequired]="true"
                >Select Parcel Manager</nz-form-label
              >
              <nz-form-control>
                <nz-select
                  [nzLoading]="
                    parcelManagersQuery.isFetching() ||
                    parcelManagersQuery.isRefetching()
                  "
                  [nzDisabled]="parcelManagersQuery.isFetching()"
                  formControlName="parcelManagerId"
                  nzNotFoundContent="No assignable parcel managers. You may have to add a new parcel manager user"
                  [nzPlaceHolder]="
                    parcelManagersQuery.isFetching()
                      ? 'Loading assignable managers...'
                      : 'Choose a parcel manager...'
                  "
                >
                  <nz-option
                    *ngFor="let su of parcelManagers"
                    [nzLabel]="su.name"
                    [nzValue]="su.id"
                  ></nz-option>
                </nz-select>
              </nz-form-control>
            </nz-form-item>

            <nz-form-item>
              <nz-form-label>Notes (Optional)</nz-form-label>
              <nz-form-control>
                <textarea
                  nz-input
                  formControlName="notes"
                  rows="3"
                  placeholder="Add any notes about this assignment..."
                ></textarea>
              </nz-form-control>
            </nz-form-item>
          </form>
        </nz-spin>
      </div>
    </nz-modal>
  `,
})
export class AssignParcelManagerComponent implements OnInit, OnChanges {
  private notification = inject(NzNotificationService);
  private fb = inject(FormBuilder);
  private tenantUsersApi = inject(TenantUsersApi);
  private modalErrorHandler = useModalErrorHandler();

  @Input() show = false;
  terminal = input.required<PlaceInListDto>();
  @Output() hide = new EventEmitter(true);
  @Output() assignmentCompleted = new EventEmitter<void>();

  form!: FormGroup;
  isSubmitting = signal(false);

  // Error state management
  showTerminalsError = signal(false);
  terminalsErrorDetails: ProblemDetail | null = null;

  parcelManagersQuery = injectQuery(() => ({
    queryKey: ['parcel-managers', this.terminal()?.id],
    queryFn: () =>
      lastValueFrom(
        this.tenantUsersApi.tenantUsersControllerGetAssignableParcelManagersForTerminal({
          terminalId: this.terminal()?.id || '',
        }),
      ),
    enabled: !!this.terminal()?.id,
    retry: 1,
  }));

  get parcelManagers() {
    return this.parcelManagersQuery.data() ?? [];
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show'] && this.show) {
      this.resetForm();
    }
  }

  private initForm() {
    this.form = this.fb.group({
      parcelManagerId: ['', [Validators.required]],
      notes: [''],
      forceReplace: [true],
    });
  }

  private resetForm() {
    this.form.reset({
      parcelManagerId: '',
      notes: '',
      forceReplace: true,
    });
    this.isSubmitting.set(false);
  }

  onHide() {
    this.resetForm();
    this.hide.emit();
  }

  onOk() {
    if (this.form.valid && !this.isSubmitting()) {
      this.isSubmitting.set(true);
      const formValue = this.form.value;
      const selectedManager = this.parcelManagers.find((u: TenantUsersDto) => u.id === formValue.parcelManagerId);
      if (!selectedManager) {
        this.notification.error('No Parcel Manager Selected', 'Please select a parcel manager.');
        this.isSubmitting.set(false);
        return;
      }
      const payload: AssignUserToTerminalDto = {
        userId: selectedManager.id,
        terminalId: this.terminal().id,
        notes: formValue.notes || undefined,
        forceReplace: formValue.forceReplace || true,
      };
      this.tenantUsersApi
        .tenantUsersControllerAssignUserToTerminal({
          id: selectedManager.id,
          body: payload,
        })
        .toPromise()
        .then(() => {
          this.isSubmitting.set(false);
          this.notification.success(
            '✅ Parcel Manager Assigned',
            `${selectedManager.firstName} ${selectedManager.surname} has been successfully assigned as Parcel Manager to this terminal.`,
          );
          this.assignmentCompleted.emit();
          this.onHide();
        })
        .catch((error) => {
          this.isSubmitting.set(false);
          const result = this.modalErrorHandler.handleModalError(error, {
            modalName: 'assign-parcel-manager',
            action: 'assign-parcel-manager',
          });
          if (!result.shouldHandleGlobally) {
            this.notification.error('❌ Assignment Failed', result.userMessage);
          }
        });
    }
  }

  // Error handling methods
  onRetryTerminals() {
    this.showTerminalsError.set(false);
    this.parcelManagersQuery.refetch();
  }

  onDismissTerminalsError() {
    this.showTerminalsError.set(false);
  }
}
